"use client";

import { useState, useCallback, useRef } from "react";
import { RateLimitDialog } from "@/components/pricing/RateLimitDialog";
import { RateLimitInfo, extractRateLimitInfo } from "@/lib/rateLimit/types";
import { detectRateLimit } from "@/lib/rateLimit/detection";

// Re-export for backward compatibility
export type { RateLimitInfo };

export function useRateLimit() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const isHandlingRef = useRef(false); // Prevent concurrent handling

  const handleRateLimitError = useCallback(
    async (error: unknown, response?: Response): Promise<boolean> => {
      // Prevent concurrent rate limit error handling
      if (isHandlingRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useRateLimit] Already handling rate limit error, skipping');
        }
        return false;
      }

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[useRateLimit] handleRateLimitError called', {
          hasError: !!error,
          errorType: error?.constructor?.name,
          responseStatus: response?.status,
          errorMessage: error && typeof error === 'object' && 'message' in error 
            ? String((error as { message?: unknown }).message)?.substring(0, 100) 
            : undefined,
        });
      }

      // Use centralized detection
      const { isRateLimit, isMonthlyLimit } = detectRateLimit(error, response);
      
      if (!isRateLimit) {
        return false;
      }

      isHandlingRef.current = true;

      try {
        // Extract rate limit info using centralized utility
        const info = extractRateLimitInfo(error, response);
        
        if (!info) {
          // If we can't extract info but detected rate limit, create minimal info
          const err = error as { status?: number; message?: string };
          setRateLimitInfo({
            limit: null,
            used: null,
            remaining: null,
            exceeded: true,
            isMonthlyLimit: isMonthlyLimit,
          });
        } else {
          setRateLimitInfo({
            ...info,
            isMonthlyLimit: isMonthlyLimit,
          });
        }

        setIsDialogOpen(true);

        if (process.env.NODE_ENV === 'development') {
          console.log('[useRateLimit] Rate limit dialog opened', {
            limit: rateLimitInfo?.limit,
            used: rateLimitInfo?.used,
            remaining: rateLimitInfo?.remaining,
            isMonthlyLimit,
          });
        }

        return true;
      } finally {
        // Reset handling flag after a short delay to prevent rapid re-triggering
        setTimeout(() => {
          isHandlingRef.current = false;
        }, 1000);
      }
    },
    [rateLimitInfo]
  );

  const handleDialogClose = useCallback((open: boolean) => {
    setIsDialogOpen(open);
    // Optionally clear rate limit info when dialog is closed
    // This allows the dialog to be shown again if a new rate limit error occurs
    if (!open) {
      // Keep the info for potential retry scenarios, but close the dialog
      // setRateLimitInfo(null);
    }
  }, []);

  const RateLimitDialogComponent = rateLimitInfo ? (
    <RateLimitDialog
      open={isDialogOpen}
      onOpenChange={handleDialogClose}
      monthlyLimit={rateLimitInfo.limit}
    />
  ) : null;

  if (process.env.NODE_ENV === 'development') {
    console.log('[useRateLimit] Render check', {
      hasRateLimitInfo: !!rateLimitInfo,
      isDialogOpen,
      willRenderDialog: !!RateLimitDialogComponent,
      limit: rateLimitInfo?.limit,
      isMonthlyLimit: rateLimitInfo?.isMonthlyLimit,
    });
  }

  return {
    handleRateLimitError,
    RateLimitDialogComponent,
    isDialogOpen,
    setIsDialogOpen: handleDialogClose,
    rateLimitInfo,
  };
}
